import { useEffect, useId, useState } from "react";

import { Input } from "@/components/ui/input";

/**
 * Đọc một giờ người dùng gõ theo kiểu tự nhiên, trả "HH:mm" (24 giờ) hoặc null khi chưa đọc được.
 *
 * <p>Nhận: "6" → 06:00, "06" → 06:00, "630" / "0630" → 06:30, "6:30" / "6h30" / "6.30" → 06:30,
 * "18" → 18:00, "1830" → 18:30, "6h" → 06:00. Từ chối giờ > 23 hoặc phút > 59.
 */
export function parseTime(raw: string): string | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return null;
  let h: number;
  let m: number;
  const sep = s.match(/^(\d{1,2})[:h.](\d{0,2})$/);
  if (sep) {
    h = Number(sep[1]);
    m = sep[2] ? Number(sep[2].padEnd(2, "0")) : 0;
  } else if (/^\d{1,4}$/.test(s)) {
    if (s.length <= 2) {
      h = Number(s);
      m = 0;
    } else {
      h = Number(s.slice(0, s.length - 2));
      m = Number(s.slice(-2));
    }
  } else {
    return null;
  }
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Gợi ý mỗi 30 phút trong ngày, cho danh sách thả xuống của ô nhập. */
const SUGGESTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  return `${String(h).padStart(2, "0")}:${i % 2 === 0 ? "00" : "30"}`;
});

/**
 * Ô nhập giờ 24 giờ, gõ nhanh — thay cho {@code <input type="time">} của trình duyệt (hiện SA/CH theo
 * ngôn ngữ máy, phải gõ đúng từng phần giờ/phút/buổi).
 *
 * <p>Giá trị vào/ra là "HH:mm" hoặc "" — cùng hợp đồng với ô giờ cũ, nên thay thế không đổi gì ở
 * nơi gọi. {@code onChange} chạy NGAY khi chuỗi gõ đọc được thành một giờ (để form kiểm tra trong
 * lúc nhập); rời ô thì chuỗi được chuẩn hoá về "HH:mm". Gõ chưa đọc được thì viền đỏ, giá trị cũ giữ
 * nguyên. Có danh sách gợi ý mỗi 30 phút.
 */
export function TimeInput({
  value,
  onChange,
  id,
  disabled,
  required,
  className,
  placeholder = "VD: 07:30",
  ...aria
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  placeholder?: string;
  "aria-label"?: string;
}) {
  const listId = useId();
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  // Giá trị từ ngoài đổi (nạp dữ liệu, reset form) → hiện giá trị mới khi không đang gõ.
  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const parsed = parseTime(draft);
  const invalid = draft.trim() !== "" && parsed === null;

  return (
    <>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        list={listId}
        value={draft}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        className={(invalid ? "border-destructive " : "") + (className ?? "")}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          if (next.trim() === "") onChange("");
          else {
            const p = parseTime(next);
            if (p !== null) onChange(p);
          }
        }}
        onBlur={() => {
          setFocused(false);
          if (parsed !== null) setDraft(parsed);
        }}
        {...aria}
      />
      <datalist id={listId}>
        {SUGGESTIONS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </>
  );
}
